import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import HeaderSection from "../../components/HeaderSection/HeaderSection";
import BottomSection from "../../components/BottomSection/BottomSection";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHashtag } from "@fortawesome/free-solid-svg-icons";
import { useTheme } from "../../context/ThemeContext";

const QRCodeGenerator = () => {
    useTheme();
    const [inputText, setInputText] = useState("");
    const [isGenerating, setIsGenerating] = useState(false);
    const [showQR, setShowQR] = useState(false);
    const [error, setError] = useState("");
    const inputRef = useRef(null);

    const generateQR = () => {
        if (!inputText.trim()) {
            setError("Please enter text or URL");
            inputRef.current.focus();
            return;
        }

        setIsGenerating(true);
        setError("");
        setShowQR(false);

        setTimeout(() => {
            setIsGenerating(false);
            setShowQR(true);
        }, 500);
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter") {
            generateQR();
        }
    };

    useEffect(() => {
        inputRef.current.focus();
    }, []);

    return (
        <>
            <HeaderSection toolId="qrCodeGenerator" />

            <div className="max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4">
                <main className="bg-white rounded-xl dark:bg-gray-800 shadow-sm dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
                        <h1 className="text-2xl font-bold mb-2 flex items-center">
                            <FontAwesomeIcon
                                icon={faHashtag}
                                className="mr-3"
                            />
                            QR Code Generator
                        </h1>
                        <p className="opacity-90">
                            Convert any text or URL into a QR code
                        </p>
                    </div>
                    <div className=" px-5 mx-auto flex flex-col md:flex-row pb-12 gap-6 items-start justify-center mt-8">
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, ease: "easeOut" }}
                            className="w-full max-w-4xl mx-auto bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 rounded-xl shadow-2xl border border-green-200 overflow-hidden"
                        >
                            <div className="p-6 sm:p-8">
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.4 }}
                                    className="space-y-4"
                                >
                                    <div>
                                        <label
                                            htmlFor="qrInput"
                                            className="block text-sm font-medium text-green-800 mb-2"
                                        >
                                            Enter text or URL
                                        </label>
                                        <input
                                            ref={inputRef}
                                            id="qrInput"
                                            type="text"
                                            value={inputText}
                                            onChange={(e) =>
                                                setInputText(e.target.value)
                                            }
                                            onKeyDown={handleKeyPress}
                                            placeholder="e.g. https://example.com"
                                            className="w-full px-4 py-3 rounded-lg bg-green-50 border border-green-200 text-green-900 placeholder-green-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                                        />
                                        {error && (
                                            <motion.p
                                                initial={{ opacity: 0, y: -10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                className="text-red-500 text-sm mt-1"
                                            >
                                                {error}
                                            </motion.p>
                                        )}
                                    </div>

                                    <AnimatePresence>
                                        {showQR && inputText && (
                                            <motion.div
                                                initial={{
                                                    opacity: 0,
                                                    height: 0,
                                                }}
                                                animate={{
                                                    opacity: 1,
                                                    height: "auto",
                                                }}
                                                exit={{ opacity: 0, height: 0 }}
                                                transition={{ duration: 0.3 }}
                                                className="flex flex-col items-center justify-center overflow-hidden"
                                            >
                                                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                                                    <QRCodeSVG
                                                        value={inputText.trim()}
                                                        size={200}
                                                        bgColor="#ecfdf5"
                                                        fgColor="#10b981"
                                                        level="H"
                                                        includeMargin={false}
                                                        className="w-full h-auto rounded"
                                                    />
                                                </div>
                                                <motion.button
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                    onClick={() =>
                                                        setShowQR(false)
                                                    }
                                                    className="mt-3 text-sm text-green-600 hover:text-green-800 transition-colors"
                                                >
                                                    Hide QR Code
                                                </motion.button>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            </div>

                            <div className="px-6 pb-6 sm:px-8 sm:pb-8">
                                <motion.button
                                    onClick={generateQR}
                                    disabled={isGenerating}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className={`w-full py-3.5 px-6 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2
                  ${
                      isGenerating
                          ? "bg-green-700 cursor-not-allowed text-white"
                          : "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 shadow-lg dark:shadow-gray-900/30 dark:shadow-gray-900/30 dark:shadow-gray-900/30 dark:shadow-gray-900/30 dark:shadow-gray-900/30 hover:shadow-green-500/30 text-white"
                  }`}
                                >
                                    {isGenerating ? (
                                        <>
                                            <svg
                                                className="animate-spin h-5 w-5 text-white"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                ></circle>
                                                <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                ></path>
                                            </svg>
                                            Generating...
                                        </>
                                    ) : (
                                        <>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-5 w-5"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M3 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm2 2V5h1v1H5zM3 13a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1v-3zm2 2v-1h1v1H5zM13 3a1 1 0 00-1 1v3a1 1 0 001 1h3a1 1 0 001-1V4a1 1 0 00-1-1h-3zm1 2v1h1V5h-1z"
                                                    clipRule="evenodd"
                                                />
                                                <path d="M11 4a1 1 0 10-2 0v1a1 1 0 002 0V4zM10 7a1 1 0 011 1v1h2a1 1 0 110 2h-3a1 1 0 01-1-1V8a1 1 0 011-1zM16 9a1 1 0 100 2 1 1 0 000-2zM9 13a1 1 0 011-1h1a1 1 0 110 2v2a1 1 0 11-2 0v-3zM7 11a1 1 0 100-2H5a1 1 0 100 2h2zM14 13a1 1 0 011-1h3a1 1 0 110 2h-3a1 1 0 01-1-1zm-7-1a1 1 0 100 2h1a1 1 0 100-2H7z" />
                                            </svg>
                                            Generate QR Code
                                        </>
                                    )}
                                </motion.button>
                            </div>
                        </motion.div>
                    </div>
                </main>
            </div>
            <BottomSection toolId="qrCodeGenerator" />
        </>
    );
};

export default QRCodeGenerator;
